'use client';

import { WrappedEvent } from '@velocity-exchange/sdk';
import {
	MarketId,
	DLOB_SERVER_WEBSOCKET_UTILS,
	DlobServerChannel,
	ResultSlotIncrementer,
	MultiplexWebSocket,
} from '@velocity-exchange/common';
import { useContext, useEffect, useRef } from 'react';
import { useInterval } from 'react-use';
import { useCurrentUserForMarketOrderEventsRef } from '../../components/MarketOrderToasts/MarketOrderToastEventEmitterProvider';
import { dlog } from '../../dev';
import Env from '../../environmentVariables/EnvironmentVariables';
import {
	DriftBlockchainEventSubjectContext,
	MERGE_USER_AND_GLOBAL_EVENT_SUBJECTS,
} from '../../providers/driftEvents/driftEventSubjectProvider';
import useInfoForCurrentlySelectedMarket from '../useInfoForCurrentlySelectedMarket';
import useEventsSubscriptionStateStore from '../../stores/useEventsSubscriptionStateStore';
import { deserializeTradeResponse } from '../dlob/dlobServerUtils';

/**
 * SEE ui/src/hooks/driftEvents/README.md for explanation of how this hook links with rest of event syncing
 */

const DEV_TRIGGER_FALLBACKS = false;

const websocketUrl = Env.dlobWsServerToUse;

const resultIncrementer = new ResultSlotIncrementer();

const UPDATE_TICK_MS = 100; // We want the tick rate for this to be fast, because the purpose of the websocket is to be highlighy responsive. But we still use a tick-rate to prevent UI lag during cascades of state changes.

const HEARTBEAT_TIMEOUT_MS = 10000; // Trigger fallback if no heartbeat received within 10 seconds
const HEARTBEAT_CHECK_INTERVAL_MS = 1000; // Check for heartbeats every second
const HEARTBEAT_GRACE_PERIOD_MS = 15_000; // On startup, we give the websocket a grace period to start sending heartbeats

const UPDATES_BUFFER: {
	marketId: MarketId;
	data: WrappedEvent<'OrderActionRecord'>;
}[] = [];
const WS_RECONNECT_TIMES = 3;

const useSyncRecentTradesWithDlobServerWebsocket = () => {
	const currentUserRef = useCurrentUserForMarketOrderEventsRef();

	const subscriptionSetupTimeRef = useRef<number>(Date.now());
	const lastHeartbeatRef = useRef<number>(Date.now());
	const lastFallbackTimeRef = useRef<number>(Date.now());
	const hasFallenBackBeforeRef = useRef<boolean>(false);

	const currentMarket = useInfoForCurrentlySelectedMarket();
	const currentMarketId = currentMarket?.info?.marketId;

	const isEnabled = useEventsSubscriptionStateStore(
		(s) =>
			s.globalEventsState.subscriptionType === 'dlob-server-trade-publisher'
	);

	const driftEventSubjectContext = useContext(
		DriftBlockchainEventSubjectContext
	);

	const currentSubscriptionRef = useRef<{
		unsubscribe: () => void;
	}>(undefined);

	const teardownSubscription = () => {
		dlog(
			`optimised_event_subscriptions::dlob-server-websocket`,
			`tearing_down_subscription`
		);

		if (!currentSubscriptionRef.current) {
			return;
		}

		currentSubscriptionRef.current.unsubscribe();
		currentSubscriptionRef.current = undefined;
	};

	const handleDatasourceFallback = () => {
		dlog(
			`optimised_event_subscriptions::dlob-server-websocket`,
			`recent_trades_dlob_server_websocket_fallback_triggered`
		);
		hasFallenBackBeforeRef.current = true;
		lastFallbackTimeRef.current = Date.now();

		useEventsSubscriptionStateStore.getState().handleGlobalEventsFallback();

		teardownSubscription();
	};

	const handleNewTrade = (
		marketId: MarketId,
		data: WrappedEvent<'OrderActionRecord'>
	) => {
		dlog(
			`optimised_event_subscriptions::dlob-server-websocket`,
			`received_trade`
		);

		UPDATES_BUFFER.push({ marketId, data });
	};

	const bufferUpdateMutex = useRef(false);
	const wsErrorCounter = useRef(0);

	const appendTradeRecord = (
		newOrderActionRecord: WrappedEvent<'OrderActionRecord'>
	) => {
		const globalFillEventSubject =
			driftEventSubjectContext.globalFillEventSubject;

		const eventIsForCurrentUser =
			currentUserRef.current &&
			(newOrderActionRecord.taker?.equals(currentUserRef.current) ||
				newOrderActionRecord.maker?.equals(currentUserRef.current));

		if (!globalFillEventSubject) {
			return;
		}

		dlog(
			`optimised_event_subscriptions::dlob-server-websocket`,
			`piping::dlob_server_websocket_recent_trade=>global_fill_subject`
		);

		globalFillEventSubject.next(newOrderActionRecord);

		if (MERGE_USER_AND_GLOBAL_EVENT_SUBJECTS && eventIsForCurrentUser) {
			dlog(
				`optimised_event_subscriptions::dlob-server-websocket`,
				`piping::dlob_server_websocket_recent_trade=>user_event_subject`
			);
			driftEventSubjectContext.userEventSubject?.next(newOrderActionRecord);
		}
	};

	const runUpdates = () => {
		if (bufferUpdateMutex.current) return;

		bufferUpdateMutex.current = true;

		// Get the buffered updates to handle
		const updatesToHandle = [...UPDATES_BUFFER];

		// Clear the buffer
		UPDATES_BUFFER.splice(0, UPDATES_BUFFER.length);

		updatesToHandle.forEach((update) => appendTradeRecord(update.data));

		bufferUpdateMutex.current = false;
	};

	// This loops through at the desired tick rate, updating recent trades with the latest updates. The reason for doing this is to prevent cascades of store state changes for each individual websocket response.
	useInterval(() => {
		runUpdates();
	}, UPDATE_TICK_MS);

	// Add heartbeat monitoring interval
	useInterval(() => {
		// Skip heartbeat checks if the websocket is disabled
		if (!isEnabled) return;

		// Skip heartbeat checks during the grace period. Only allow the grace period when we haven't fallen back yet
		if (
			!hasFallenBackBeforeRef.current &&
			Date.now() - subscriptionSetupTimeRef.current < HEARTBEAT_GRACE_PERIOD_MS
		) {
			return;
		}

		if (currentMarketId && isEnabled) {
			const timeSinceLastHeartbeat = Date.now() - lastHeartbeatRef.current;

			if (timeSinceLastHeartbeat > HEARTBEAT_TIMEOUT_MS) {
				dlog(
					`optimised_event_subscriptions::dlob-server-websocket`,
					`${currentMarketId.key} NO HEARTBEAT RECEIVED FOR ${timeSinceLastHeartbeat}ms: TRIGGERING FALLBACK`
				);
				handleDatasourceFallback();
			}
		}
	}, HEARTBEAT_CHECK_INTERVAL_MS);

	const setUpSubscription = () => {
		subscriptionSetupTimeRef.current = Date.now();
		lastHeartbeatRef.current = Date.now();

		if (!currentMarketId || !isEnabled) {
			throw new Error(
				'Cannot set up DLOB websocket subscription: missing marketId or subscription is disabled'
			);
		}

		dlog(
			`optimised_event_subscriptions::dlob-server-websocket`,
			`setting_up_subscription`
		);

		try {
			const onErrorCallback = () => {
				dlog(
					`optimised_event_subscriptions::dlob-server-websocket`,
					`${currentMarketId.key} CAUGHT ERROR IN DLOB-SERVER WEBSOCKET: FALLING BACK`
				);
				handleDatasourceFallback();
			};

			const { unsubscribe } = MultiplexWebSocket.createWebSocketSubscription<{
				channel: DlobServerChannel;
				data: string;
			}>({
				wsUrl: websocketUrl,
				subscriptionId: `${websocketUrl}_recent_trades_${currentMarketId.key}`,
				enableHeartbeatMonitoring: true,
				subscribeMessage: JSON.stringify(
					DLOB_SERVER_WEBSOCKET_UTILS.getSubscriptionProps({
						type: 'trades',
						market: currentMarketId,
					})
				),
				unsubscribeMessage: JSON.stringify(
					DLOB_SERVER_WEBSOCKET_UTILS.getUnsubscriptionProps({
						type: 'trades',
						market: currentMarketId,
					})
				),
				onMessage: (message: { channel: DlobServerChannel; data: string }) => {
					if (message.channel === 'heartbeat') {
						if (DEV_TRIGGER_FALLBACKS) {
							if (Math.random() > 0.5) {
								return;
							}
						}

						dlog(
							`optimised_event_subscriptions::dlob-server-websocket`,
							`recent_trades_dlob_server_websocket_heartbeat_received`
						);
						lastHeartbeatRef.current = Date.now();
						return;
					}

					const orderActionRecord = deserializeTradeResponse(
						JSON.parse(message.data)
					);

					const resultKey = `${message.channel}_${currentMarketId.key}`;

					const lastSlotReceived = orderActionRecord.slot;

					const validResult = resultIncrementer.handleResult(
						resultKey,
						lastSlotReceived
					);

					if (!validResult) {
						return; // Skip results which aren't slot-increasing
					}

					handleNewTrade(currentMarketId, orderActionRecord);
				},
				messageFilter: (message: { channel: string; data: string }) => {
					return DLOB_SERVER_WEBSOCKET_UTILS.getMessageFilter({
						type: 'trades',
						market: currentMarketId,
					})(message);
				},
				onError: onErrorCallback,
				errorMessageFilter: (message?: any) => {
					if (message.error) {
						console.log(`ERROR IN ERROR MESSAGE FILTER`, message);
						return true;
					}

					return false;
				},
			});

			if (!unsubscribe) {
				return;
			}

			currentSubscriptionRef.current = {
				unsubscribe,
			};
		} catch (e) {
			// try to resubscribe to websocket unless the error is hitting repeatedly
			if (wsErrorCounter.current > WS_RECONNECT_TIMES) {
				dlog(
					`optimised_event_subscriptions::dlob-server-websocket`,
					`${currentMarketId.key} CAUGHT ERROR SUBSCRIBING TO DLOB-SERVER WEBSOCKET: FALLING BACK`
				);
				wsErrorCounter.current = 0;
				handleDatasourceFallback();
			} else {
				dlog(
					`optimised_event_subscriptions::dlob-server-websocket`,
					`${currentMarketId.key} CAUGHT ERROR SUBSCRIBING TO DLOB-SERVER WEBSOCKET: RETRYING`
				);
				wsErrorCounter.current++;
				setUpSubscription();
			}

			return;
		}
	};

	// Setup subscription when necessary
	useEffect(() => {
		if (!currentMarketId) return;
		if (!isEnabled) return;

		setUpSubscription();

		return () => {
			teardownSubscription();
		};
	}, [currentMarketId, isEnabled]);
};

export default useSyncRecentTradesWithDlobServerWebsocket;
